import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { EmptyState } from '@ecom/ui-kit';
import { type ReactElement, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  CHART_RANGES,
  type ChartRange,
  useDailySalesSeriesQuery,
  useRevenuePeriodsQuery,
} from '../api';
import { AlertsPanel } from '../components/AlertsPanel';
import { type KpiTrend } from '../components/KpiCard';
import { KpiGrid } from '../components/KpiGrid';
import { OrderStatusPanel } from '../components/OrderStatusPanel';
import { QuickActions } from '../components/QuickActions';
import { SalesTrendChart } from '../components/SalesTrendChart';
import { TopProducts } from '../components/TopProducts';
import { WelcomeCard } from '../components/WelcomeCard';

import { SkIcon } from '@/components/ui/SkIcon';
import { SHOP_TIME_ZONE } from '@/features/analytics/activity';
import { useMerchantAnalyticsQuery } from '@/features/analytics/api';
import { useInventoryOverviewQuery } from '@/features/inventory/api';
import { formatCatalogPrice } from '@/features/products/format-price';
import { ShareStoreCard } from '@/features/share/components/ShareStoreCard';
import { useActiveShop } from '@/features/shops/use-active-shop';
import { pluralKey } from '@/lib/plural';

function computeTodayTrend(
  points: ReturnType<typeof useDailySalesSeriesQuery>['points'],
  t: ReturnType<typeof useI18n>['t'],
): KpiTrend | undefined {
  const today = points.at(-1);
  const yesterday = points.at(-2);
  if (today === undefined || yesterday === undefined) return undefined;

  if (yesterday.revenue > 0) {
    const changePercent = ((today.revenue - yesterday.revenue) / yesterday.revenue) * 100;
    const rounded = Math.round(changePercent);
    if (rounded === 0) return { direction: 'flat', label: t('merchant.dashboard.trendStable') };
    return {
      direction: rounded > 0 ? 'up' : 'down',
      label: t('merchant.dashboard.trendChange', {
        value: `${rounded > 0 ? '+' : ''}${String(rounded)}`,
      }),
    };
  }
  if (today.revenue > 0) {
    return { direction: 'up', label: t('merchant.dashboard.trendNewActivity') };
  }
  return undefined;
}

function intlLocale(locale: string): string {
  if (locale === 'ar') return 'ar-TN';
  if (locale === 'en') return 'en-GB';
  return 'fr-TN';
}

/** The hero's period selector (Figma « Souk 2.0 »): a button and a small menu of the three periods. */
function RangeSelect({
  value,
  onChange,
}: {
  value: ChartRange;
  onChange: (range: ChartRange) => void;
}): ReactElement {
  const { t, locale } = useI18n();
  const [open, setOpen] = useState(false);
  const label = (range: ChartRange): string =>
    t(pluralKey(locale, range, 'merchant.dashboard.rangeDays'), { count: range });
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event: PointerEvent): void => {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        className="sk-range-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${t('merchant.dashboard.rangeAria')} : ${label(value)}`}
        onClick={() => {
          setOpen((current) => !current);
        }}
      >
        <SkIcon name="calendar" />
        <span>{label(value)}</span>
        <SkIcon name="chevron" size={13} className="ms-1 rotate-90" />
      </button>
      {open ? (
        <div className="sk-range-menu" role="menu">
          {CHART_RANGES.map((range) => (
            <button
              key={range}
              type="button"
              role="menuitemradio"
              aria-checked={range === value}
              onClick={() => {
                onChange(range);
                setOpen(false);
              }}
            >
              {label(range)}
              {range === value ? <SkIcon name="check" size={14} /> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The merchant's daily « how is my shop doing » screen, integrated from the Figma design « Redesign Souk Merchant
 * Dashboard » (« Souk 2.0 »): the editorial hero (greeting with the shop's name, today's date and the last refresh in
 * Tunis time, the period selector, « Ajouter un produit »), the four headline cards, « Évolution des ventes » beside
 * « État des commandes », « À votre attention » beside the shortcuts, the share panel, and the best sellers.
 *
 * Every figure is real: revenue computed client-side from real orders (features/dashboard/api.ts), order counts and
 * active products from `GET /merchant/analytics` (S17), stock from the inventory overview. The design's demonstration
 * data (and anything it showed without a source here — a monthly objective, a help desk) is not reproduced.
 */
export function DashboardPage(): ReactElement {
  const { t, locale } = useI18n();
  const [range, setRange] = useState<ChartRange>(14);
  const analyticsQuery = useMerchantAnalyticsQuery();
  const revenuePeriods = useRevenuePeriodsQuery();
  const rangeSeries = useDailySalesSeriesQuery(range);
  const inventory = useInventoryOverviewQuery();
  const { activeMembership } = useActiveShop();

  const intl = intlLocale(locale);
  const today = new Intl.DateTimeFormat(intl, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: SHOP_TIME_ZONE,
  }).format(new Date());
  const updatedAt =
    analyticsQuery.dataUpdatedAt > 0
      ? new Intl.DateTimeFormat(intl, {
          hour: '2-digit',
          minute: '2-digit',
          hourCycle: 'h23',
          timeZone: SHOP_TIME_ZONE,
        }).format(new Date(analyticsQuery.dataUpdatedAt))
      : null;
  const shopName = activeMembership?.legalName ?? null;

  const hero = (
    <section className="sk-hero">
      <div className="relative z-[1] min-w-0">
        <h1>
          {shopName !== null
            ? t('merchant.dashboard.hello', { name: shopName })
            : t('merchant.dashboard.title')}{' '}
          <em>{t('merchant.dashboard.heroLead')}</em>
        </h1>
        <p>
          <span className="inline-block first-letter:uppercase">{today}</span>
          {updatedAt !== null ? (
            <>
              <span className="mx-1.5" aria-hidden="true">
                ·
              </span>
              {t('merchant.dashboard.updatedAt', { time: updatedAt })}
            </>
          ) : null}
        </p>
      </div>
      <div className="sk-hero-actions">
        <RangeSelect value={range} onChange={setRange} />
        <Link to="/products/new" className="sk-btn sk-btn--primary">
          <SkIcon name="plus" />
          <span>{t('merchant.dashboard.addProduct')}</span>
        </Link>
      </div>
    </section>
  );

  if (analyticsQuery.isError) {
    return (
      <div className="flex flex-col gap-5">
        {hero}
        <WelcomeCard />
        <EmptyState
          title={
            analyticsQuery.error.status === 403
              ? t('merchant.common.accessDenied')
              : t('merchant.dashboard.loadError')
          }
          description={
            analyticsQuery.error.status === 403
              ? t('merchant.dashboard.accessDeniedDescription')
              : t(resolveErrorMessageKey(analyticsQuery.error))
          }
        />
      </div>
    );
  }

  const analytics = analyticsQuery.data;
  const lowStock = inventory.rows.filter((row) => row.stock.state === 'low_stock').length;
  const outOfStock = inventory.rows.filter((row) => row.stock.state === 'out_of_stock').length;
  const rangeOrders = rangeSeries.isPending
    ? null
    : rangeSeries.points.reduce((sum, point) => sum + point.orderCount, 0);

  return (
    <div className="flex flex-col">
      {hero}
      <div className="mb-4 empty:hidden">
        <WelcomeCard />
      </div>

      <KpiGrid
        todayRevenue={
          revenuePeriods.isPending ? null : formatCatalogPrice(revenuePeriods.todayRevenue)
        }
        todayTrend={computeTodayTrend(rangeSeries.points, t)}
        todayHint={
          revenuePeriods.isPending
            ? undefined
            : t(
                pluralKey(
                  locale,
                  revenuePeriods.todayOrderCount,
                  'merchant.dashboard.kpiTodayHint',
                ),
                {
                  count: revenuePeriods.todayOrderCount,
                },
              )
        }
        monthRevenue={
          revenuePeriods.isPending ? null : formatCatalogPrice(revenuePeriods.monthRevenue)
        }
        monthHint={
          revenuePeriods.isPending
            ? undefined
            : t(
                pluralKey(
                  locale,
                  revenuePeriods.monthOrderCount,
                  'merchant.dashboard.kpiMonthHint',
                ),
                {
                  count: revenuePeriods.monthOrderCount,
                },
              )
        }
        rangeOrders={rangeOrders}
        rangeHint={t('merchant.dashboard.kpiOrdersRangeHint', {
          days: t(pluralKey(locale, range, 'merchant.dashboard.rangeDays'), { count: range }),
        })}
        points={rangeSeries.points}
        activeProducts={analytics === undefined ? null : analytics.activeProductCount}
        lowStock={lowStock}
        outOfStock={outOfStock}
      />

      <div className="sk-analytics-grid">
        <SalesTrendChart days={range} />
        {analytics === undefined ? (
          <div className="ecom-skeleton min-h-[416px] rounded-[22px]" aria-hidden="true" />
        ) : (
          <OrderStatusPanel entries={analytics.ordersByStatus} />
        )}
      </div>

      <div className="sk-bottom-grid">
        <AlertsPanel />
        <QuickActions />
      </div>

      <ShareStoreCard context="dashboard" part="panel" />

      <div className="mt-[14px]">
        {analytics === undefined ? (
          <div className="ecom-skeleton h-[260px] rounded-[22px]" aria-hidden="true" />
        ) : (
          <TopProducts products={analytics.topProducts} />
        )}
      </div>
    </div>
  );
}
